import { useAuth0 } from '@auth0/auth0-react';

import { createChipLabelText, createChipTooltip } from '@/util';

import { TemplatePlaceholder } from '@/types';
import { DomainCode, domainCodeAtom } from '@australiangreens/ag-internal-components';
import { Search } from '@mui/icons-material';
import TemplateCapableFetchAutocomplete from '@/components/TemplateCapableFetchAutocomplete';
import { SearchTemplatePageContext } from '@/context/SearchTemplateContext';
import { useAtomValue } from 'jotai';
import { useContext } from 'react';
import { getEventsContaining } from '@/apis/events';
import { labelSorting } from '@/util/labelSorting';
import { EventRecordResult } from '@/types/filterCriteriaRecords';

type EventsProps = {
  onChange: (newValue: EventRecordResult[] | TemplatePlaceholder) => void;
  value?: EventRecordResult[] | TemplatePlaceholder;
  minLength?: number;
  eventLabel?: string;
  dataTestidPrefix?: string;
  loadingText?: string;
  noOptionsText?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  isAdvancedSearch?: boolean;
};

const Events = ({
  value = [],
  onChange,
  minLength = 3,
  eventLabel = 'Event name(s) (optional)',
  dataTestidPrefix,
  loadingText,
  noOptionsText = 'No events found',
  error = false,
  helperText = '',
  isAdvancedSearch,
}: EventsProps) => {
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  const inSearchTemplate = useContext(SearchTemplatePageContext);
  const lookupEvents = async (lookupValue: string) => {
    const token = await getAccessTokenSilently();
    return (await getEventsContaining(token, lookupValue, domainCode as DomainCode))
      .map((x) => {
        const { id, title, startDate = '' } = x;
        const chipTooltipText = createChipTooltip(title, startDate);
        const chipLabel = createChipLabelText(title, startDate);
        return {
          id: id,
          label: chipTooltipText,
          chipLabel: chipLabel,
          tooltipContent: chipTooltipText,
        };
      })
      .sort(labelSorting);
  };

  return (
    <TemplateCapableFetchAutocomplete
      sx={isAdvancedSearch ? { width: '45ch' } : {}}
      lookup={lookupEvents}
      minLength={minLength}
      label={eventLabel}
      value={value}
      onChange={onChange}
      data-testid={dataTestidPrefix}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      popupIcon={<Search />}
      disableIconFlip
      error={error}
      helperText={helperText}
      isOnTemplatePage={inSearchTemplate}
      templateBuilderDisplayName="Event attendance filter: Event"
    />
  );
};

export default Events;
